<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import { ButtonType, Header, CustomButton } from '@/components';
import ToggleSwitch from 'primevue/toggleswitch';
import { useAuthStore } from '@/stores/auth';
import { useStudyStore } from '@/stores/study';
import { useStudySettingsStore } from '@/stores/studySettings';

const studyStore = useStudyStore();
const authStore = useAuthStore();
const studySettingsStore = useStudySettingsStore();

const newStudyData = ref({
  title: '',
  long: false
});

const createStudy = async () => {
  await studyStore.createStudy({title: newStudyData.value.title, long: newStudyData.value.long ? 'long' : 'cross'});
};
const isFormDisabled = computed(() => {
  return !newStudyData.value.title.trim();
});

const resetChanges = () => {
  newStudyData.value = {
    title: '',
    long: false
  };
};

const shouldShowUpgradeMessage = computed(() => {
  const userData = authStore.userData;
  const billing = studySettingsStore.billing;

  return (
    (!(userData?.identifier !== billing?.organizationOwner) &&
      !billing?.manuallyBilled &&
      billing?.activeInvestigations &&
      billing?.activeInvestigations.length >= billing?.quotas?.plan?.studies) ||
    billing?.freeTrial
  );
});

const goToUpdatePlan = () => {
  if (studyStore.selectedStudy) {
    window.location.href = `/organizations/${studyStore.selectedStudy.organizationIdentifier}/billing`;
  }
};

onMounted(() => {
  studySettingsStore.getBilling();
});

</script>

<template>
  <div class="study-settings">
    <div class="header">
      <Header title="Create new study"></Header>
    </div>
    <div class="study-settings-container">
      <div v-if="studySettingsStore.loaders.billing.isLoading"
          class="progress-container">
        <ProgressSpinner style="width: 32px" strokeWidth="4" />
      </div>
      <div v-else class="main">
        <div v-if="studyStore.studyPermissions
            && studyStore.studyPermissions.createStudy
            && authStore.userData?.identifier !== studySettingsStore?.billing?.organizationOwner">
            <p class="case-manage-description">
              This will create a new study under the organization created by
              {{ studySettingsStore?.billing?.organizationOwnerDetails?.names }}
              {{ studySettingsStore?.billing?.organizationOwnerDetails?.email }}
            </p>
        </div>
        <div v-else>
          This will create a new study under your own organization
        </div>
        <div class="study-name">
          <div class="study-name-label">Study Name</div>
          <InputText v-model="newStudyData.title"
            class="study-name-input"
            placeholder="Enter the name of your study"/>
        </div>
        <div class="case-management">
          <div class="case-manage-label">Case Management</div>
          <div class="case-manage-description">
            Cases act as folders and they help you organize data entries
          </div>
          <div class="case-management-bar">
            <div class="switch-container">
              <ToggleSwitch v-model="newStudyData.long" />
              <label class="switch-label" v-if="newStudyData.long">ON</label>
              <label class="switch-label" v-else>OFF</label>
            </div>

            <div class="case-management-change-text">
              {{
                newStudyData.long
                  ? "Turn case management OFF when collecting self-reported data (e.g. patient-reported outcomes)."
                  : "Create cases and share them with your team."
              }}
            </div>
          </div>
        </div>
        <div v-if="shouldShowUpgradeMessage">
          &#9888; To create a new study you will need to upgrade or
          archive your current study.
          <br />
          If you need help we are here for you: support@yuzulabs.com
        </div>
        <div v-if="shouldShowUpgradeMessage" class="button-container">
          <CustomButton text="Upgrade plan"
            :icon="{ style: 'fas', icon: 'arrow-circle-right' }"
            @onClick="goToUpdatePlan"
            :type="ButtonType.OUTLINED_PRIMARY"/>
        </div>
        <div class="buttons">
          <CustomButton text="Cancel"
            @onClick="resetChanges" />
          <CustomButton text="Create study"
            :disabled="isFormDisabled"
            @onClick="createStudy"
            :type="ButtonType.POSITIVE" 
            :loading="studyStore.studyLoader.isLoading" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.study-settings {
  padding: 48px 100px 40px 80px;
}
.study-settings-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  align-items: flex-start;
  color: #272a2e;
  line-height: 20px;
  font-size: 14px;
  max-width: 900px;
  min-width: 600px;
  padding: 40px;
  border-width: 1px;
  border-color: #d9dee4;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
  b {
    margin-bottom: 10px;
    font-weight: bold;
    font-size: 16px;
  }
  .progress-container {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: span 2;
  }
}
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 20px;
}
.study-name {
  display: flex;
  flex-direction: column;
  margin: unset;

  input {
    max-width: 300px;
  }

  & + &{
    margin-top: 12px;
  }
}
.study-name-label {
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
}
.study-name-input {
  font-size: 14px;
}
.study-name-error {
  display: flex;
  justify-content: flex-start;
  span {
    color: red;
  }
}
.study-settings {
  padding: 48px 100px 40px 80px;
}
.study-settings-container {
  display: grid;
  grid-template-areas:
    "main main main side"
    "main main main side"
    "bottom bottom bottom bottom";
  grid-template-rows: auto auto auto;
  color: #272a2e;
  line-height: 20px;
  font-size: 14px;
  max-width: 900px;
  min-width: 600px;
  padding: 40px;
  border-width: 1px;
  border-color: #d9dee4;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
  b {
    margin-bottom: 10px;
    font-weight: bold;
    font-size: 16px;
  }
  .header-small {
    font-weight: bold;
  }
  .description-small {
    font-size: 12px;
    font-style: italic;
    color: #6c757d;
  }
  .progress-container {
    display: flex;
    align-items: center;
    grid-row: 1 / 3;
  }
}
.main {
  grid-area: main;
}
.side {
  grid-area: side;
}
.bottom {
  grid-area: bottom;
}
.study-name,
.case-management,
.case-management-change-text,
.archive-study,
.webhooks,
.api-key {
  display: flex;
  flex-direction: column;
  margin: 10px 0;
}
.study-name {
  margin: unset;
  margin-top: 20px;

  input {
    max-width: 300px;
  }
}
.case-management {
  padding: 48px 0 60px 0;
}
.study-name-label, .case-manage-label, .webhook-label {
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
  padding-bottom: 12px;
}
.study-name-input {
  font-size: 14px;
}
.case-manage-label, .webhook-label {
  padding: unset;
}
.case-manage-description, .webhook-description {
  color: #575e66;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 20px;
}
.switch-label {
  margin-left: 12px;
}
.entry-created, .entry-updated {

  .header-small {
    line-height: 30px;
  }

  .entry-created-input-switch {
    display: flex;
    align-items: center;

    .switch-container {
      margin-right: 32px;
    }

    .entry-created-webhook-inpit {
      font-size: 14px;
      height: 40px;
    }
  }
}
.entry-updated {
  margin-top: 20px;
}
.archive-study {
  padding: 48px 0px 60px 0px;
}
.case-management-change-text {
  text-align: left;
  justify-content: center;
  min-height: 28px;
  max-width: 400px;
  line-height: 14px;
}
.study-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.study-avatar img {
  width: 50%;
  height: auto;
  border-radius: 10%;
  border: 2px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.study-name input,
.entry-created input,
.entry-updated input,
.api-key input {
  width: 100%;
  font-size: 14px;
}
.case-management-bar,
.api-key-bar {
  display: flex;
  align-items: center;
  text-align: center;
  flex-direction: row;
  gap: 10px;
}
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 20px;
}
.archive-study :deep(button),
.study-avatar :deep(button) {
  margin: 8px 0;
  line-height: 16px;
}
.api-key :deep(button) {
  line-height: 20px;
  min-width: 161px;
  border: 1px solid #ced4da;
  background: #ffffff;
  color: #6c757d;
}
.archive-study Button {
  line-height: 20px;
  min-width: 100px;
  border: 1px solid #ced4da;
  background: #ffffff;
  color: #6c757d;
  max-width: 100px;
}
.clipboard {
  :deep(button) {
    min-width: 20px !important;
  }
  :deep(.icon) {
    margin-right: 0;
    height: 16px;
  }
}
.api-key-bar {
  button:hover {
    background-color: hsla(0, 0%, 0%, 0.15);
  }

  :deep(button):hover {
    background-color: hsla(0, 0%, 0%, 0.15);
  }
}
.switch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px 10px;
  box-sizing: border-box;
}
.study-name-error {
  display: flex;
  justify-content: flex-start;
  span {
    color: red;
  }
}
.button-container{
  margin-top: 20px;
}
</style>

